/**
 * Block-level validation rules (ADR-0101).
 *
 * A block may declare `validate(input, ctx)`: a pure function over its own
 * slot values that returns rule results. Two severities:
 *
 *  - `warning` — the value is allowed; the host shows the message next to
 *    the field and in its summary.
 *  - `error` — the value is kept only with an explicit override. Without
 *    one the document must not leave its editable state.
 *
 * An override is stored in the shared values under
 * `VALIDATION_OVERRIDES_KEY` together with a snapshot of the values the
 * rule read; it lapses by itself the moment any of them changes.
 *
 * The same evaluation runs live in an editor and authoritatively on a
 * server — the clock is injected (`RuleContext.today`), never read.
 */
import { evaluateCondition } from './slots.js';
export const VALIDATION_OVERRIDES_KEY = '__validation_overrides';
/**
 * The today of an instant in a time zone (default Europe/Stockholm) —
 * hosts call this once and pass the result in.
 */
export function createRuleContext(input) {
    const date = typeof input.now === 'string' ? new Date(input.now) : input.now;
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: input.timeZone ?? 'Europe/Stockholm',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return { today: `${get('year')}-${get('month')}-${get('day')}`, locale: input.locale ?? 'sv-SE' };
}
/** "a (b) c" → field "a c", summary "a b c". */
export function splitRuleMessage(message) {
    const tidy = (s) => s.replace(/\s{2,}/g, ' ').replace(/\s+([.,;:!?])/g, '$1').trim();
    return {
        field: tidy(message.replace(/\([^()]*\)/g, '')),
        summary: tidy(message.replace(/\(([^()]*)\)/g, '$1')),
    };
}
/** Reads the stored overrides; anything malformed is dropped. */
export function readValidationOverrides(values) {
    const raw = values?.[VALIDATION_OVERRIDES_KEY];
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const item of raw) {
        if (!item || typeof item !== 'object')
            continue;
        const { id, values: snap } = item;
        if (typeof id !== 'string' || id.length === 0 || id.length > 200 || seen.has(id))
            continue;
        if (!snap || typeof snap !== 'object' || Array.isArray(snap))
            continue;
        seen.add(id);
        out.push({ id, values: { ...snap } });
    }
    return out;
}
const normalize = (v) => (v === undefined || v === '' ? null : v);
const sameValue = (a, b) => JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
const snapshotMatches = (snapshot, current) => {
    const keys = new Set([...Object.keys(snapshot), ...Object.keys(current)]);
    for (const k of keys)
        if (!sameValue(snapshot[k], current[k]))
            return false;
    return true;
};
/**
 * Runs every block's `validate` over the composition. `values` defaults to
 * the composition's own; overrides are read from them. Instances whose
 * `includeWhen` excludes them are skipped. A throwing rule is skipped —
 * validation never breaks a document.
 */
export function evaluateCompositionRules(composition, registry, ctx, values = composition.values ?? {}) {
    const overrides = new Map(readValidationOverrides(values).map((o) => [o.id, o]));
    const issues = [];
    for (const instance of composition.blocks) {
        const definition = registry.resolve(instance.blockRef);
        if (!definition?.validate)
            continue;
        const scope = { ...values, ...instance.input };
        if (!evaluateCondition(instance.includeWhen, scope))
            continue;
        let results;
        try {
            const input = definition.parse ? definition.parse(scope) : scope;
            results = definition.validate(input, ctx);
        }
        catch {
            continue;
        }
        for (const r of results) {
            const id = `${instance.instanceId}:${r.ruleId}`;
            const slotKeys = [...r.slotKeys];
            const snapshot = Object.fromEntries(slotKeys.map((k) => [k, normalize(scope[k])]));
            const override = overrides.get(id);
            const split = splitRuleMessage(r.message);
            issues.push({
                id,
                ruleId: r.ruleId,
                instanceId: instance.instanceId,
                blockRef: instance.blockRef,
                anchorKey: slotKeys[0] ?? '',
                slotKeys,
                severity: r.severity,
                fieldMessage: split.field,
                summaryMessage: r.summaryMessage ?? split.summary,
                ...(r.fix ? { fix: { label: r.fix.label, set: { ...r.fix.set } } } : {}),
                overridden: r.severity === 'error' && !!override && snapshotMatches(override.values, snapshot),
                snapshot,
            });
        }
    }
    const live = new Set(issues.filter((i) => i.overridden).map((i) => i.id));
    return {
        issues,
        blocking: issues.filter((i) => i.severity === 'error' && !i.overridden),
        lapsed: [...overrides.keys()].filter((id) => !live.has(id)),
    };
}
/**
 * The overrides list after keeping `issue` (or releasing it with
 * `keep: false`). Lapsed entries are pruned on the way.
 */
export function withValidationOverride(values, evaluation, issue, keep = true) {
    const lapsed = new Set(evaluation.lapsed);
    const kept = readValidationOverrides(values).filter((o) => o.id !== issue.id && !lapsed.has(o.id));
    return keep ? [...kept, { id: issue.id, values: { ...issue.snapshot } }] : kept;
}
// ─── Date helpers for rules ──────────────────────────────────────────────
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** A valid calendar `YYYY-MM-DD` (first ten characters), else null. */
export function parseIsoDate(value) {
    if (typeof value !== 'string')
        return null;
    const s = value.slice(0, 10);
    const m = ISO_DATE.exec(s);
    if (!m)
        return null;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo))
        return null;
    return s;
}
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
const pad = (n, w = 2) => String(n).padStart(w, '0');
export function addDays(iso, days) {
    const [y, m, d] = iso.split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d + days));
    return `${pad(t.getUTCFullYear(), 4)}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}
/**
 * The last day of a period of `months` starting on `start` (inclusive):
 * 2026-10-20 + 12 → 2027-10-19, 2024-02-29 + 12 → 2025-02-28,
 * 2026-01-31 + 1 → 2026-02-28, 2026-02-01 + 1 → 2026-02-28.
 * Counted from the day before the start; a month-end stays a month-end.
 */
export function periodEnd(start, months) {
    const before = addDays(start, -1);
    const [y, m, d] = before.split('-').map(Number);
    const monthEnd = d === daysInMonth(y, m);
    const total = y * 12 + (m - 1) + months;
    const ty = Math.floor(total / 12);
    const tm = (total % 12) + 1;
    const dim = daysInMonth(ty, tm);
    return `${pad(ty, 4)}-${pad(tm)}-${pad(monthEnd ? dim : Math.min(d, dim))}`;
}
//# sourceMappingURL=validation.js.map